import { T, useGT } from 'gt-react';
import type { DivisionOption } from './useDivisionSelection';
import { segmentedButtonClass as buttonClass } from './segmentedButtonClass';

/**
 * Multi-select division (DataSource) selector, led by an "All" button whose
 * meaning the caller decides through `allSelected` / `onToggleAll` — the export
 * tab selects every division with it (and reads it as active exactly while every
 * one is), the catalog's Source filter clears the narrowing. Renders nothing when
 * the org has no DataSources.
 *
 * `label` names the group, for callers that show the same divisions under their
 * own word for them (the catalog calls them Source, after the column its raw
 * tables show them in).
 *
 * A division in `emptyIds` has no records in the current view: it is greyed out
 * and unselectable, so the divisions a review actually covers are readable off
 * the row rather than found by clicking into empty tables.
 *
 * `onIsolate` makes a double-click on a division select it alone — the quick
 * way from "all" to one without clicking the rest off. The two clicks before
 * it toggle the division as usual; the isolate lands last and settles the
 * selection, so the net effect is exactly that one division.
 */
export function DivisionSelector({
  dataSources,
  value,
  allSelected,
  emptyIds,
  onToggle,
  onToggleAll,
  onIsolate,
  label,
}: {
  dataSources: DivisionOption[];
  value: string[];
  allSelected: boolean;
  /** Divisions the current view has no records for — greyed out. */
  emptyIds?: ReadonlySet<string>;
  onToggle: (dataSourceId: string) => void;
  onToggleAll: () => void;
  /** Double-click: select this division and nothing else. */
  onIsolate?: (dataSourceId: string) => void;
  label?: string;
}) {
  const gt = useGT();
  if (dataSources.length === 0) return null;
  // Membership set so the per-button active check is O(1) rather than scanning
  // `value` on every render of every division button.
  const selected = new Set(value);
  const emptyLabel = gt('No records in this review', {
    context: 'tooltip on a division the review has nothing under',
  });
  const isolateHint = onIsolate
    ? gt('Double-click to show only this division', {
        context:
          'tooltip on a division filter button, saying a double-click selects it alone',
      })
    : undefined;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap w-28">
        {label ?? (
          <T context="label for AS/400 export division (data source) filter group">
            Division
          </T>
        )}
      </span>
      <div className="flex rounded-md border overflow-hidden text-xs">
        <button
          type="button"
          aria-pressed={allSelected}
          className={buttonClass(0, allSelected)}
          onClick={onToggleAll}
        >
          <T context="division filter, select or clear every division">All</T>
        </button>
        {dataSources.map((dataSource, index) => {
          const isEmpty = emptyIds?.has(dataSource.id) ?? false;
          const isActive = selected.has(dataSource.id);
          return (
            <button
              key={dataSource.id}
              type="button"
              aria-pressed={isActive}
              disabled={isEmpty}
              title={isEmpty ? emptyLabel : isolateHint}
              className={buttonClass(index + 1, isActive, isEmpty)}
              onClick={() => onToggle(dataSource.id)}
              onDoubleClick={
                onIsolate ? () => onIsolate(dataSource.id) : undefined
              }
            >
              {dataSource.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}
